import React, { useState } from 'react';
import emailjs from 'emailjs-com';

const GRID_BG = {
  backgroundImage:
    'linear-gradient(rgba(201,168,76,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.12) 1px, transparent 1px)',
  backgroundSize: '60px 60px',
};

const InputField = ({ label, type = 'text', name, value, onChange, placeholder, required = true }) => (
  <div>
    <label className="block text-sm font-semibold text-navy-800 mb-2">
      {label} {required && <span className="text-gold-600">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-gold-500/40 focus:border-gold-500 transition-all bg-white"
    />
  </div>
);

const EMPTY_FORM = { name: '', email: '', phone: '', organization: '', message: '' };

const Contact = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 6000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);

    emailjs
      .sendForm('service_9p3v7qu', 'template_kf08sch', e.target, 'GPb86XvMQLwgakqu2')
      .then(
        (result) => {
          console.log('EmailJS success:', result.status);
          setSubmitting(false);
          setFormData(EMPTY_FORM);
          showToast('success', "Message sent! We'll be in touch shortly.");
        },
        (error) => {
          console.error('EmailJS error:', error);
          setSubmitting(false);
          showToast('error', 'Sending failed — please email us directly at contact@dtxdigitaltransformation.com');
        }
      );
  };

  return (
    <div className="pt-20">

      {/* Toast notification */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-4 rounded-xl shadow-2xl text-sm font-semibold max-w-sm ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.type === 'success' ? (
            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 110 18A9 9 0 0112 3z" />
            </svg>
          )}
          <span className="flex-1">{toast.message}</span>
          <button onClick={() => setToast(null)} className="opacity-70 hover:opacity-100 ml-1 shrink-0">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Page header */}
      <section className="relative bg-navy-800 py-20 overflow-hidden">
        <div className="absolute inset-0" style={GRID_BG} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-gold-500 text-sm font-bold uppercase tracking-widest mb-4">Get in Touch</p>
          <h1 className="text-4xl md:text-5xl font-black text-white font-heading mb-4">Contact Us</h1>
          <p className="text-slate-300 text-lg max-w-2xl leading-relaxed">
            Interested in partnering with DTX? Fill out the form and a member of our team will be in touch shortly.
          </p>
        </div>
      </section>

      {/* Contact content */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">

            {/* Sidebar info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-xl font-black text-navy-800 font-heading mb-5">Contact Information</h2>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gold-500/10 border border-gold-200 rounded-xl flex items-center justify-center shrink-0">
                      <span className="text-lg">📍</span>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5">Address</p>
                      <p className="text-sm text-navy-800 font-medium">43661 Catton Pl<br />Ashburn, VA 20147</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gold-500/10 border border-gold-200 rounded-xl flex items-center justify-center shrink-0">
                      <span className="text-lg">✉️</span>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5">Email</p>
                      <a
                        href="mailto:contact@dtxdigitaltransformation.com"
                        className="text-sm text-gold-600 hover:text-gold-700 font-medium break-all"
                      >
                        contact@dtxdigitaltransformation.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gold-500/10 border border-gold-200 rounded-xl flex items-center justify-center shrink-0">
                      <span className="text-lg">📞</span>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5">Phone</p>
                      <a href="tel:+17033282940" className="text-sm text-navy-800 font-medium">703-328-2940</a>
                      <span className="text-slate-400 text-sm"> / </span>
                      <a href="tel:+17033282748" className="text-sm text-navy-800 font-medium">703-328-2748</a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <h3 className="font-bold text-navy-800 font-heading mb-4">Contracting Officers</h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-4">
                  Looking to verify our certifications, NAICS codes, or SAM.gov registration? We're happy to assist.
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex gap-2">
                    <span className="text-slate-400 text-xs uppercase w-20 shrink-0 pt-0.5">UEI</span>
                    <span className="text-navy-800 font-mono font-semibold">ESNGY5HK7QS1</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-slate-400 text-xs uppercase w-20 shrink-0 pt-0.5">CAGE</span>
                    <span className="text-navy-800 font-mono font-semibold">64GE5</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {['SBA 8(a)', 'EDWOSB', 'WOSB', 'SAM Registered'].map((cert) => (
                  <span
                    key={cert}
                    className="text-xs border border-gold-500/40 text-gold-600 px-3 py-1 rounded-full font-semibold"
                  >
                    {cert}
                  </span>
                ))}
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <InputField
                        label="Full Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                      />
                      <InputField
                        label="Email Address"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@agency.gov"
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <InputField
                        label="Phone Number"
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 000-0000"
                      />
                      <InputField
                        label="Organization / Agency"
                        name="organization"
                        value={formData.organization}
                        onChange={handleChange}
                        placeholder="Agency or company name"
                        required={false}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-navy-800 mb-2">
                        Message <span className="text-gold-600">*</span>
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder="Tell us about your requirements or how we can help..."
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-gold-500/40 focus:border-gold-500 transition-all bg-white resize-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-navy-800 hover:bg-navy-700 disabled:opacity-60 text-white py-4 rounded-xl font-bold text-sm transition-colors shadow-md"
                    >
                      {submitting ? 'Sending...' : 'Send Message'}
                    </button>
                    <p className="text-xs text-slate-400 text-center">
                      This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.
                    </p>
                  </form>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default Contact;
